import { ReactNode } from "react";
import { Row } from "react-bootstrap";
import { useProjectStore } from "../../store/ProjectStore";
import { I18n } from "@/i18n/I18n";
import { useWorkspaceStore } from "../../store/WorkspaceStore";
import { FileSubjectTypes } from "@/types/fileTypes";
import { HierarchyCard } from "./HierarchyCard";
import { CreateHierarchyModelButton } from "../buttons/CreateHierarchyModelButton";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useProjectLockStore } from "@/frontend/store/ProjectLockStore";
import { Project, ProjectParentType } from "@/backend/models/project";

/** `actions` render on the same row as the create button, ahead of it. */
export function ProjectCards(props: { projects: Project[], parent?: { id: string, propelOrgId: string }, parentType: ProjectParentType, hideCreateButton?: boolean, actions?: ReactNode }) {
  const { projects, parent, parentType, hideCreateButton, actions } = props;
  const projectLocks = useProjectLockStore(state => state.projectLocks);
  const user = useGlobalStore(state => state.userClass);
  const { deleteProject, updateProject, refetchProject } = useProjectStore();

  const canCreateProject = parent ? parent?.propelOrgId === user?.getOrgId() : true;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2">
        {actions}
        { !hideCreateButton && canCreateProject && <CreateProjectButton parentId={parent?.id} parentType={parentType} />}
      </div>
      {projects.length === 0 && (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
          <h3>{I18n.get('noProjects')}</h3>
        </div>
      )}
      <Row xs={1} md={2} lg={4} className="py-2 g-4">
        {projects.map((project) => (
          <HierarchyCard
            key={project.id}
            model={project}
            titleLink={`/projects/${project.id}`}
            modelLabel={I18n.get('projectUppercase')}
            updateModel={updateProject}
            deleteModel={deleteProject}
            subjectType={FileSubjectTypes.PROJECT}
            refetchSubject={refetchProject}
            viewOnly={user?.getOrgId() !== project.propelOrgId}
            lock={projectLocks.find(lock => lock.projectId === project.id)}
          />
        ))}
      </Row>
    </div>
  );
}

function CreateProjectButton(props: { parentId?: string, parentType: ProjectParentType }) {
  const { parentId, parentType } = props;
  const workspaces = useWorkspaceStore(state => state.workspaces);
  const createProject = useProjectStore(state => state.createProject);
  const refetchProject = useProjectStore(state => state.refetchProject);

  const handleCreate = async (title: string, description: string, selectedWorkspaceId: string): Promise<string | undefined> => {
    const response =  await createProject({
      title: title,
      description,
      parentId: selectedWorkspaceId,
      parentType,
    });

    return response.project?.id;
  };

  return (
    <CreateHierarchyModelButton 
      createModel={(title, description, selectedWorkspaceId) => handleCreate(title, description, selectedWorkspaceId)} 
      modelLabel={parentType === ProjectParentType.WORKSPACE ? I18n.get('projectUppercase') : I18n.get('projectTemplate')} 
      refetchSubject={refetchProject} 
      subjectType={FileSubjectTypes.PROJECT}
      parents={workspaces}
      parentLabel={I18n.get('workspace')}
      defaultParentId={parentId}
    />
  );
}
